import React, { createContext, useContext } from 'react';
import * as authApi from '../api/auth';
import { getToken, onUnauthorized } from '../api/client';

// The signed-in user, backed by a JWT in localStorage.
//
// Before this existed the user lived in App's React state, so every refresh
// logged you out. On boot we ask GET /login/me with the stored token: that both
// restores the session and proves the token is still valid, so a stale or
// revoked token can't leave the app in a half-signed-in state.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  // Distinguishes "no session" from "we don't know yet", so guarded routes can
  // wait instead of bouncing a signed-in user to /login on every refresh.
  const [restoring, setRestoring] = React.useState(() => !!getToken());

  React.useEffect(() => {
    if (!getToken()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const restored = await authApi.me();
        if (!cancelled) setUser(restored);
      } catch (e) {
        // Only throw the token away when the SERVER says it is bad. A network
        // failure (status 0, API not running) or a 5xx says nothing about the
        // token, and discarding it there logs people out every time the backend
        // hiccups — they'd have to re-enter their password to recover from a
        // blip. The token is kept and the user simply stays un-restored until
        // the next reload.
        if (e && (e.status === 401 || e.status === 403)) authApi.logout();
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // A 401 that the client judged to be a dead session — it filters out
  // role-mismatch 401s, which the API also returns 401 for.
  React.useEffect(() => onUnauthorized(() => {
    authApi.logout();
    setUser(null);
    setRestoring(false);
  }), []);

  const login = async (email, password) => {
    const signedIn = await authApi.login(email, password);
    setUser(signedIn);
    return signedIn;
  };

  const logout = () => {
    authApi.logout();
    setUser(null);
  };

  const value = React.useMemo(
    () => ({
      user,
      restoring,
      login,
      logout,
      isAdmin: user?.role === 'admin',
      /**
       * The super admin (DOK).
       *
       * Tested on the ID rather than the role because that is exactly what the
       * server tests: every `/dok` route runs `dok_middleware.checkRole`, which
       * is `req.admin.id !== 1 -> 403`. The `role` column does say 'teacher' for
       * this account, but it is not what grants the permission — gating the UI on
       * the role would offer buttons that 403, and gating it on the id cannot
       * disagree with the API.
       */
      isDok: user?.role === 'admin' && String(user.id) === '1',
    }),
    [user, restoring], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
